import { ChevronDownIcon as ChevronDown, Typography } from "@packages/ds";
import styled, { css as styledCSS } from "styled-components";

import { Link } from "ds/Button";
import Checkbox from "ds/Checkbox";
import * as Dialog from "ds/Dialog";
import * as Dropdown from "ds/Dropdown";
import Input from "ds/Input";
import Modal from "ds/Modal";
import Select from "ds/Select";
import Chart from "src/organization/pages/project/environment/metrics/components/Chart";

export const WorkerBadge = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

export const Actions = styled.div`
  display: flex;
  align-items: center;
  flex-direction: row;
  gap: 4px;
`;
export const configureResources = styled(Link)`
  width: 32px;
  height: 32px;
  &:hover {
    background-color: var(--mode-bg-neutral-weaker);
    width: 32px;
    height: 32px;
    border-radius: 50%;
  }
  &:hover::after {
    display: none;
  }
`;

export const MoreAction = styled(Dropdown.Trigger)``;
export const DescriptionWrapper = styled.div`
  text-align: center;

  & p {
    margin: 32px 0 40px 0;
  }
`;

export const Layout = styled.div`
  padding-top: 20px;
  padding-right: 32px;
  padding-bottom: 20px;
  padding-left: 32px;
  border-radius: 2px;
  background-color: var(--section-snow-background-color, var(--snow));
  position: relative;
`;

export const ChartLayout = styled(Layout)`
  padding-top: 24px;
  margin-bottom: 8px;
  margin-top: -4px;
`;

export const Title = styled.div`
  font-family: Open Sans;
  font-style: normal;
  font-weight: bold;
  font-size: 15px;
  line-height: 24px;
  color: var(--header-ebony-color, var(--ebony));
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 24px;
`;

export const Header = styled.div<{ $isOpen: boolean }>`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  ${({ $isOpen }) =>
    !$isOpen &&
    styledCSS`
      margin-bottom: 22px;
    `};
`;

const __verticalSeparator = (separator = 16) => styledCSS`
  position: relative;
  :before {
    left: -${separator}px;
    position: absolute;
    content: "";
    height: 100%;
    width: 1px;
    background-color: var(--section-periwinkle-grey-background-color,var(--periwinkle-grey));
  }
`;

const __horizontalSeparator = (
  leftShift = 0,
  addedWidth = 32,
  bottom = 13
) => styledCSS`
  position: relative;
  ::after {
    content: "";
    position: absolute;
    bottom: -${bottom}px;
    left: -${leftShift}px;
    background-color: var(--section-periwinkle-grey-background-color,var(--periwinkle-grey));
    height: 1px;
    width: calc(100% + ${addedWidth}px);
  }
`;

export const Grid = styled.div`
  display: grid;
  row-gap: 24px;
  grid-template-columns: minmax(0, 1fr);
  > :nth-child(1n):not(:last-child) {
    position: relative;
  }

  @media (max-width: 924px) {
    > :nth-child(1n):not(:last-child) {
      ${__horizontalSeparator(0, 0)};
    }
  }

  @media (min-width: 925px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    column-gap: 32px;

    > :nth-child(3n + 1):not(:last-child) {
      ${__horizontalSeparator()};
    }

    > :nth-child(3n + 2) {
      ${__horizontalSeparator(34, 34)};
    }
  }

  @media (min-width: 925px) and (max-width: 1349px) {
    > :nth-child(2n) {
      ${__verticalSeparator()};
    }

    // hides horizontal separator for all items in last row
    > :nth-child(2n + 1):nth-last-child(-n + 2),
    > :nth-child(2n + 1):nth-last-child(-n + 2) ~ div {
      :after {
        display: none;
      }
    }
  }

  @media (min-width: 1350px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));

    > :nth-child(3n + 1):not(:last-child) {
      ${__horizontalSeparator()};
    }
    > :nth-child(3n + 2) {
      ${__horizontalSeparator(34, 68)};
    }
    > :nth-child(3n + 3) {
      ${__horizontalSeparator(32, 32)};
    }

    // hides horizontal separator for all items in last row
    > :nth-child(3n + 1):nth-last-child(-n + 3),
    > :nth-child(3n + 1):nth-last-child(-n + 3) ~ div {
      :after {
        display: none;
      }
    }
  }

  @media (min-width: 1350px) and (max-width: 1500px) {
    > :nth-child(3n + 2),
    > :nth-child(3n + 3) {
      ${__verticalSeparator()};
    }
  }

  @media (min-width: 1500px) {
    column-gap: 64px;
    grid-template-columns: repeat(3, minmax(0, 1fr));

    > :nth-child(3n + 2),
    > :nth-child(3n + 3) {
      ${__verticalSeparator(32)};
    }
  }
`;

export const GridLG = styled(Grid)`
  gap: 64px;

  @media (max-width: 924px) {
    > :nth-child(1n):not(:last-child) {
      ${__horizontalSeparator(0, 0, 32)};
    }
  }

  @media (min-width: 925px) {
    column-gap: 64px;

    > :nth-child(3n + 1):not(:last-child) {
      ${__horizontalSeparator(0, 32, 32)};
    }

    > :nth-child(3n + 2) {
      ${__horizontalSeparator(34, 34, 32)};
    }
  }

  @media (min-width: 925px) and (max-width: 1349px) {
    > :nth-child(2n) {
      ${__verticalSeparator(32)};
    }
  }

  @media (min-width: 1350px) {
    > :nth-child(3n + 1):not(:last-child) {
      ${__horizontalSeparator(0, 32, 32)};
    }
    > :nth-child(3n + 2) {
      ${__horizontalSeparator(34, 68, 32)};
    }
    > :nth-child(3n + 3) {
      ${__horizontalSeparator(32, 32, 32)};
    }
  }

  @media (min-width: 1350px) and (max-width: 1500px) {
    > :nth-child(3n + 2),
    > :nth-child(3n + 3) {
      ${__verticalSeparator(32)};
    }
  }

  @media (min-width: 1500px) {
    column-gap: 64px;

    > :nth-child(3n + 2),
    > :nth-child(3n + 3) {
      ${__verticalSeparator(32)};
    }
  }
`;

export const ModalWrapper = styled(Modal)`
  top: 0;
  bottom: 0;
  min-height: 100vh;
  padding-bottom: 0;
`;

export const ContainerChart = styled(Chart)`
  margin-left: -32px;
  margin-right: -32px;
`;

export const AppsAndResources = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;
export const ThresholdsValues = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;
export const UpperThreshold = styled.div`
  display: flex;
  align-items: center;
  margin: 16px 0 0 40px;
`;
export const ValuesWrapper = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;
export const LowerThreshold = styled.div`
  display: flex;
  align-items: center;
  margin-left: 40px;
`;

export const ThresholdInput = styled(Input)`
  max-width: 60px;
  margin-left: 8px;

  label {
    margin-top: -24px;
  }

  input {
    max-width: 100%;
  }
`;

export const DurationOptions = styled(Select)`
  width: 120px;
`;
export const CoolDownDurationOptions = styled(Select)`
  width: 120px;
`;
export const ActionButtons = styled.div`
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-top: 31px;
`;
export const ApplicationLabel = styled(Typography.BodyText)`
  color: var(--mode-text-neutral-weakest);
  margin-right: 16px;
`;
export const ShowMetrics = styled(ChevronDown)<{ $isOpen: boolean }>`
  cursor: pointer;
  width: 32px;
  height: 32px;
  padding: 4px;

  ${({ $isOpen }) => $isOpen && `transform: rotate(180deg);`}

  &:hover {
    background-color: var(--mode-bg-neutral-weaker);
    border-radius: 50%;
  }
`;

export const EnableThresholds = styled.div`
  display: flex;
  position: relative;
  gap: 4px;
`;

export const SelectedAppContainer = styled.div`
  display: flex;
`;
export const EnableCheckbox = styled(Checkbox)`
  margin: 0;
`;
export const EnableThresholdsDialog = styled(Dialog.Root)`
  position: relative;
`;
export const MinInstancesDialog = styled(Dialog.Root)``;
export const MaxInstancesDialog = styled(Dialog.Root)``;
export const Content = styled(Dialog.Content)`
  height: 160px;
  position: absolute;
  left: 30px;
  &[data-side="bottom"] {
    top: 340px;
  }
`;

export const MaxContent = styled(Dialog.Content)`
  height: 160px;
  position: absolute;
  &[data-side="bottom"] {
    top: 260px;
    right: -98vw;
  }
`;
export const MinContent = styled(Dialog.Content)`
  height: 160px;
  position: absolute;
  &[data-side="bottom"] {
    top: 260px;
    right: -80vw;
  }
`;

export const MaximumInstancesWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  position: relative;
`;
export const MinimumInstancesWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  position: relative;
`;

export const InstancesWrapper = styled.div`
  display: flex;
  gap: 24px;
`;
export const MaximumInstances = styled(Input)`
  max-width: 60px;
  display: flex;

  label span {
    display: block;
    min-width: 130px;
  }

  input {
    max-width: 100%;
  }
`;
export const MinimumInstances = styled(Input)`
  max-width: 60px;
  display: flex;

  label span {
    display: block;
    min-width: 130px;
  }

  input {
    max-width: 100%;
  }
`;

export const ContentWrapper = styled.div`
  margin: 0 128px;
`;
